import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { axios, queryClient } from '@apps-common/lib'
import { IMMSiteEvent } from '@apps-common/types/my-monitoring'

export const QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE = (identifiers?: string, siteId?: string, skip?: number, take?: number) =>
  ['getMyMonitoringSiteEventsInfinite', identifiers, siteId, skip, take].filter(Boolean)

const getSiteEventsInfinite = async (identifiers: string, siteId: string, skip: number, take: number): Promise<IMMSiteEvent[]> =>
  axios
    .get(`/my-monitoring/sites/GetSiteEvents?identifiers=${identifiers}&siteId=${siteId}&skip=${skip}&take=${take}`)
    .then((res) => res.data?.data)

type Props = {
  config?: any
  identifiers: string
  siteId: string
  take: number
}

export const useMyMonitoringGetSiteEventsInfinite = ({ config, identifiers, siteId, take }: Props) => {
  const { isLoading, isFetching, isRefetching, isError, data, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    ...(config || {}),
    queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE(identifiers, siteId, take),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => getSiteEventsInfinite(identifiers, siteId, pageParam as number, take),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length < take) {
        return undefined
      }

      return allPages?.reduce((acc, curr) => acc + (curr?.length || 0), 0)
    }
  })

  const refetchOverride = async () => {
    await queryClient.cancelQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE(identifiers, siteId) })
    queryClient.removeQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE(identifiers, siteId) })
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE(identifiers, siteId) })
    refetch()
  }

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_EVENTS_INFINITE() })
    },
    []
  )

  return { isLoading, isFetching, isRefetching, isError, data, refetch: refetchOverride, fetchNextPage, hasNextPage }
}
